/**
 * @file useAutomatedPricing.tsx
 * Hook that helps calculate quote pricing when a vehicle
 * has an automated price added to it.
 */
import first from "lodash/first";
import isNil from "lodash/isNil";

import { useOperator } from "./useOperator";
import { PickUpVariant, PricingLayout, PromoCode } from "../../types";
import { CustomerPricingType } from "./utils";
import { CreateStopState } from "pages/new/context/initialState";

type UseAutomatedPricingParams = {
  trips: {
    stops: CreateStopState[];
    routes: {
      vehicle?: {
        id: string;
        baseRateAutomation?: {
          total: number;
        };
        returnBaseRateAutomation?: {
          total: number;
        };
      };
      pricing?: { driverGratuityAmt?: number };
    }[];
  }[];
  driverGratuityPctCustomerInput?: number | "cash";
  pickUpVariantSelected: PickUpVariant;
  userAppliedPromoCode?: Pick<
    PromoCode,
    "id" | "promoCodeName" | "promoCodeAmt" | "promoCodePercent"
  >;
  additonalItemsForOutboundTrip?: {
    childSeats: {
      boosterSeatAmt?: number;
      boosterSeatQuantity?: number;
      forwardFacingSeatAmt?: number;
      forwardFacingSeatQuantity?: number;
      rearFacingSeatAmt?: number;
      rearFacingSeatQuantity?: number;
    };
  };
};

function useAutomatedPricing(
  params: UseAutomatedPricingParams
): CustomerPricingType {
  const {
    trips,
    pickUpVariantSelected,
    userAppliedPromoCode,
    additonalItemsForOutboundTrip,
  } = params;

  // convert cash gratuity to 0 for price calculations
  const driverGratuityPctCustomerInput =
    params.driverGratuityPctCustomerInput === "cash"
      ? 0
      : params.driverGratuityPctCustomerInput;

  const { baseRateAutomation, returnBaseRateAutomation } = first(
    first(trips).routes
  ).vehicle;

  const automatedBaseRate = baseRateAutomation?.total || null;
  const returnAutomatedBaseRate = returnBaseRateAutomation?.total || null;

  // hooks
  const {
    operator: { pricingLayout },
  } = useOperator();

  const {
    tripPricing: outboundTripPricing,
    tripTotalAmt: outboundTripTotalAmt,
  } = calculateCreateTripPricing({
    baseRateAmt: automatedBaseRate || 0,
    pricingLayout,
    driverGratuityPctCustomerInput,
    pickUpVariantSelected,
    isAirportPickUpStop:
      !!trips[0].stops[0].trackedFlight ||
      !!trips[0].stops[0].pickUpGooglePlaceTypes?.includes("airport") ||
      !!trips[0].stops[0].airport?.icaoCode,
    userAppliedPromoCode, // Apply promo code to outbound trip
    childSeatInput: additonalItemsForOutboundTrip?.childSeats,
  });

  let returnTripPricing;
  let returnTripTotalAmt;

  if (returnAutomatedBaseRate !== null) {
    ({ tripPricing: returnTripPricing, tripTotalAmt: returnTripTotalAmt } =
      calculateCreateTripPricing({
        baseRateAmt: returnAutomatedBaseRate,
        pricingLayout,
        driverGratuityPctCustomerInput,
        pickUpVariantSelected,
        isAirportPickUpStop:
          !!trips[1]?.stops[0]?.trackedFlight ||
          !!trips[1].stops[0].pickUpGooglePlaceTypes?.includes("airport") ||
          !!trips[1]?.stops[0]?.airport?.icaoCode,
        userAppliedPromoCode, // Apply promo code to return trip as well
        childSeatInput: additonalItemsForOutboundTrip?.childSeats,
      }));
  }

  const orderTotalAmt = outboundTripTotalAmt + (returnTripTotalAmt || 0);

  return {
    outboundTripPricing,
    outboundTripTotalAmt,
    returnTripPricing,
    returnTripTotalAmt,
    orderTotalAmt,
  };
}

export { useAutomatedPricing };

type CalculateTripPricingParams = {
  baseRateAmt: number;
  pricingLayout: PricingLayout;
  driverGratuityPctCustomerInput?: number;
  pickUpVariantSelected?: PickUpVariant;
  isAirportPickUpStop?: boolean;
  userAppliedPromoCode?: Pick<
    PromoCode,
    "id" | "promoCodeName" | "promoCodeAmt" | "promoCodePercent"
  >;
  childSeatInput?: {
    boosterSeatAmt?: number;
    boosterSeatQuantity?: number;
    forwardFacingSeatAmt?: number;
    forwardFacingSeatQuantity?: number;
    rearFacingSeatAmt?: number;
    rearFacingSeatQuantity?: number;
  };
};

type CalculatePricingParams = {
  percent?: number;
  amt?: number;
  baseRateAmt: number;
};

const calculatePricing = (params: CalculatePricingParams) => {
  const { percent, amt, baseRateAmt } = params;

  if (isNil(amt) && isNil(percent)) return null;

  const amount = !isNil(percent) ? percent * baseRateAmt : amt;
  return isNaN(amount)
    ? 0
    : !isNil(amount)
    ? Math.round(amount * 100) / 100
    : null;
};

export const calculateCreateTripPricing = (
  params: CalculateTripPricingParams
) => {
  const {
    baseRateAmt,
    pricingLayout,
    driverGratuityPctCustomerInput,
    pickUpVariantSelected,
    isAirportPickUpStop,
    userAppliedPromoCode,
    childSeatInput: {
      boosterSeatAmt,
      boosterSeatQuantity,
      forwardFacingSeatAmt,
      forwardFacingSeatQuantity,
      rearFacingSeatAmt,
      rearFacingSeatQuantity,
    } = {},
  } = params;

  const {
    driverGratuityPercent,
    driverGratuityAmt,
    promoDiscountPercent,
    promoDiscountAmt,
    taxPercent,
    taxAmt,
    tollsAmt,
    meetGreetAmt,
    otherPercent,
    otherAmt,
    otherName,
    other2Percent,
    other2Amt,
    other2Name,
    other3Percent,
    other3Amt,
    other3Name,
    other4Percent,
    other4Amt,
    other4Name,
    other5Percent,
    other5Amt,
    other5Name,
    other6Percent,
    other6Amt,
    other6Name,
    stcPercent,
    stcAmt,
    creditCardServiceFeePercent,
    creditCardServiceFeeAmt,
    calculatedOnSubtotalItems: calcOnSubtotalItems,
  } = pricingLayout || {};

  const calcOnSubtotalSet = new Set<string>(calcOnSubtotalItems || []);

  // In case where trip already has driverGratuityAmt, we use it
  const normalizedDriverGratuity =
    driverGratuityAmt || driverGratuityPercent
      ? {
          amt: driverGratuityAmt,
          percent: driverGratuityPercent,
        }
      : {
          percent: driverGratuityPctCustomerInput / 100,
        };

  // First pass: resolve subtotal-group items using baseRateAmt
  const driverGratuityValueFirstPass = !calcOnSubtotalSet.has("driverGratuity")
    ? calculatePricing({ baseRateAmt, ...normalizedDriverGratuity })
    : null;

  const promoDiscountValueFirstPass = !calcOnSubtotalSet.has("promoDiscount")
    ? calculatePricing({ baseRateAmt, percent: promoDiscountPercent, amt: promoDiscountAmt })
    : null;

  const taxValueFirstPass = !calcOnSubtotalSet.has("tax")
    ? calculatePricing({ baseRateAmt, percent: taxPercent, amt: taxAmt })
    : null;

  const tollsValue = calculatePricing({ baseRateAmt, amt: tollsAmt });

  const meetGreetValue = calculatePricing({
    baseRateAmt,
    amt:
      isAirportPickUpStop &&
      pickUpVariantSelected === PickUpVariant.MeetGreet
        ? meetGreetAmt
        : null,
  });

  const otherValueFirstPass = !calcOnSubtotalSet.has("other")
    ? calculatePricing({ baseRateAmt, percent: otherPercent, amt: otherAmt })
    : null;

  const other2ValueFirstPass = !calcOnSubtotalSet.has("other2")
    ? calculatePricing({ baseRateAmt, percent: other2Percent, amt: other2Amt })
    : null;

  const other3ValueFirstPass = !calcOnSubtotalSet.has("other3")
    ? calculatePricing({ baseRateAmt, percent: other3Percent, amt: other3Amt })
    : null;

  const other4ValueFirstPass = !calcOnSubtotalSet.has("other4")
    ? calculatePricing({ baseRateAmt, percent: other4Percent, amt: other4Amt })
    : null;

  const other5ValueFirstPass = !calcOnSubtotalSet.has("other5")
    ? calculatePricing({ baseRateAmt, percent: other5Percent, amt: other5Amt })
    : null;

  const other6ValueFirstPass = !calcOnSubtotalSet.has("other6")
    ? calculatePricing({ baseRateAmt, percent: other6Percent, amt: other6Amt })
    : null;

  const stcValueFirstPass = !calcOnSubtotalSet.has("stc")
    ? calculatePricing({ baseRateAmt, percent: stcPercent, amt: stcAmt })
    : null;

  const creditCardServiceFeeValueFirstPass = !calcOnSubtotalSet.has("creditCardServiceFee")
    ? calculatePricing({ baseRateAmt, percent: creditCardServiceFeePercent, amt: creditCardServiceFeeAmt })
    : null;

  // Compute subtotal
  let subtotalAmt = baseRateAmt;
  if (calcOnSubtotalSet.size > 0) {
    if (!calcOnSubtotalSet.has("driverGratuity")) subtotalAmt += driverGratuityValueFirstPass || 0;
    if (!calcOnSubtotalSet.has("promoDiscount")) subtotalAmt -= promoDiscountValueFirstPass || 0;
    if (!calcOnSubtotalSet.has("tax")) subtotalAmt += taxValueFirstPass || 0;
    if (!calcOnSubtotalSet.has("tolls")) subtotalAmt += tollsValue || 0;
    if (!calcOnSubtotalSet.has("meetGreet")) subtotalAmt += meetGreetValue || 0;
    if (!calcOnSubtotalSet.has("other")) subtotalAmt += otherValueFirstPass || 0;
    if (!calcOnSubtotalSet.has("other2")) subtotalAmt += other2ValueFirstPass || 0;
    if (!calcOnSubtotalSet.has("other3")) subtotalAmt += other3ValueFirstPass || 0;
    if (!calcOnSubtotalSet.has("other4")) subtotalAmt += other4ValueFirstPass || 0;
    if (!calcOnSubtotalSet.has("other5")) subtotalAmt += other5ValueFirstPass || 0;
    if (!calcOnSubtotalSet.has("other6")) subtotalAmt += other6ValueFirstPass || 0;
    if (!calcOnSubtotalSet.has("stc")) subtotalAmt += stcValueFirstPass || 0;
    if (!calcOnSubtotalSet.has("creditCardServiceFee")) subtotalAmt += creditCardServiceFeeValueFirstPass || 0;
  }

  // Second pass: resolve calculated-on-subtotal items
  const finalPromoDiscountValue = calcOnSubtotalSet.has("promoDiscount")
    ? calculatePricing({ baseRateAmt: subtotalAmt, percent: promoDiscountPercent, amt: promoDiscountAmt })
    : promoDiscountValueFirstPass;

  const finalTaxValue = calcOnSubtotalSet.has("tax")
    ? calculatePricing({ baseRateAmt: subtotalAmt, percent: taxPercent, amt: taxAmt })
    : taxValueFirstPass;

  const finalOtherValue = calcOnSubtotalSet.has("other")
    ? calculatePricing({ baseRateAmt: subtotalAmt, percent: otherPercent, amt: otherAmt })
    : otherValueFirstPass;

  const finalOther2Value = calcOnSubtotalSet.has("other2")
    ? calculatePricing({ baseRateAmt: subtotalAmt, percent: other2Percent, amt: other2Amt })
    : other2ValueFirstPass;

  const finalOther3Value = calcOnSubtotalSet.has("other3")
    ? calculatePricing({ baseRateAmt: subtotalAmt, percent: other3Percent, amt: other3Amt })
    : other3ValueFirstPass;

  const finalOther4Value = calcOnSubtotalSet.has("other4")
    ? calculatePricing({ baseRateAmt: subtotalAmt, percent: other4Percent, amt: other4Amt })
    : other4ValueFirstPass;

  const finalOther5Value = calcOnSubtotalSet.has("other5")
    ? calculatePricing({ baseRateAmt: subtotalAmt, percent: other5Percent, amt: other5Amt })
    : other5ValueFirstPass;

  const finalOther6Value = calcOnSubtotalSet.has("other6")
    ? calculatePricing({ baseRateAmt: subtotalAmt, percent: other6Percent, amt: other6Amt })
    : other6ValueFirstPass;

  const finalStcValue = calcOnSubtotalSet.has("stc")
    ? calculatePricing({ baseRateAmt: subtotalAmt, percent: stcPercent, amt: stcAmt })
    : stcValueFirstPass;

  const finalCreditCardServiceFeeValue = calcOnSubtotalSet.has("creditCardServiceFee")
    ? calculatePricing({ baseRateAmt: subtotalAmt, percent: creditCardServiceFeePercent, amt: creditCardServiceFeeAmt })
    : creditCardServiceFeeValueFirstPass;

  const finalDriverGratuityValue = calcOnSubtotalSet.has("driverGratuity")
    ? calculatePricing({ baseRateAmt: subtotalAmt, ...normalizedDriverGratuity })
    : driverGratuityValueFirstPass;

  const tripPricing = {
    baseRate: {
      label: "Base Rate",
      value: baseRateAmt,
    },
    driverGratuity: {
      label: "Driver Gratuity",
      value: finalDriverGratuityValue,
    },
    promoDiscount: {
      label: "Discount",
      value: finalPromoDiscountValue,
      isNeg: true,
    },
    tax: {
      label: "Tax",
      value: finalTaxValue,
    },
    tolls: {
      label: "Tolls",
      value: tollsValue,
    },
    meetGreet: {
      label: "Meet & Greet",
      value: meetGreetValue,
      selected:
        isAirportPickUpStop &&
        pickUpVariantSelected === PickUpVariant.MeetGreet,
    },
    rearFacingSeat: {
      label:
        rearFacingSeatQuantity > 1
          ? `${rearFacingSeatQuantity} X Rear-Facing Seat`
          : "Rear-Facing Seat",
      value:
        rearFacingSeatAmt === 0
          ? 0
          : calculatePricing({ baseRateAmt, amt: rearFacingSeatAmt }),
      quantity: rearFacingSeatQuantity,
    },
    forwardFacingSeat: {
      label:
        forwardFacingSeatQuantity > 1
          ? `${forwardFacingSeatQuantity} X Forward-Facing Seat`
          : "Forward-Facing Seat",
      value:
        forwardFacingSeatAmt === 0
          ? 0
          : calculatePricing({ baseRateAmt, amt: forwardFacingSeatAmt }),
      quantity: forwardFacingSeatQuantity,
    },
    boosterSeat: {
      label:
        boosterSeatQuantity > 1
          ? `${boosterSeatQuantity} X Booster Seat`
          : "Booster Seat",
      value:
        boosterSeatAmt === 0
          ? 0
          : calculatePricing({ baseRateAmt, amt: boosterSeatAmt }),
      quantity: boosterSeatQuantity,
    },
    other: {
      label: otherName || "Other",
      value: finalOtherValue,
    },
    other2: {
      label: other2Name || "Other 2",
      value: finalOther2Value,
    },
    other3: {
      label: other3Name || "Other 3",
      value: finalOther3Value,
    },
    other4: {
      label: other4Name || "Other 4",
      value: finalOther4Value,
    },
    other5: {
      label: other5Name || "Other 5",
      value: finalOther5Value,
    },
    other6: {
      label: other6Name || "Other 6",
      value: finalOther6Value,
    },
    stc: {
      label: "Standard Transportation Costs",
      value: finalStcValue,
    },
    creditCardServiceFee: {
      label: "Credit Card Service Fee",
      value: finalCreditCardServiceFeeValue,
    },
    promoCode: userAppliedPromoCode
      ? {
          label: `Promo Code (${userAppliedPromoCode.promoCodeName})`,
          value: calculatePricing({
            baseRateAmt,
            percent: userAppliedPromoCode.promoCodePercent,
            amt: userAppliedPromoCode.promoCodeAmt,
          }),
          isNeg: true,
          removeField: "promoCodeCustomerInput",
        }
      : {
          label: "Promo Code",
          value: null,
        },
  };

  const tripTotalAmt =
    tripPricing.baseRate.value +
    tripPricing.driverGratuity.value -
    tripPricing.promoDiscount.value +
    tripPricing.tax.value +
    tripPricing.tolls.value +
    tripPricing.meetGreet.value +
    tripPricing.boosterSeat.value +
    tripPricing.forwardFacingSeat.value +
    tripPricing.rearFacingSeat.value +
    tripPricing.other.value +
    tripPricing.other2.value +
    tripPricing.other3.value +
    tripPricing.other4.value +
    tripPricing.other5.value +
    tripPricing.other6.value +
    tripPricing.stc.value +
    tripPricing.creditCardServiceFee.value -
    tripPricing.promoCode.value;

  return { tripPricing, tripTotalAmt };
};
